import { Fetcher } from '@this/src/util';
import moment from 'moment';
import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { useTheme } from '@material-ui/core/styles';
import type { AITTheme } from '@this/shared/ui/theme';

function getStyles(theme: AITTheme) {
  return {
    link: {
      transition: 'opacity 0.3s',
      color: theme.tokens.colors.brand.primary,
      textDecoration: 'none',
      cursor: 'pointer'
    },
    tag: {
      border: `1px solid ${theme.tokens.colors.brand.primary}`,
      backgroundColor: theme.tokens.colors.brand.background,
      padding: '2px',
      marginRight: '2px'
    }
  };
}

import Trip from '@this/domain/trip/trip';
import type Order from '@this/domain/order';
import type OrderItem from '@this/domain/order_item';
import Arranger from '@this/domain/arranger/arranger';
import type SuppliedItem from '@this/domain/supplied_item/supplied_item';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import type PaymentMethod from '@this/src/domain/payment_method/payment_method';
import JournalTable from './journal_table/journal_table';
import TraceIdEditor from './trace_id_editor/trace_id_editor';
import TraceIdMemo from './trace_id_memo/trace_id_memo';
import OrderItemCell from './order_item_cell/order_item_cell';

interface Props {
  trip: Trip;
  suppliedItems: SuppliedItem[];
  paymentMethods: PaymentMethod[];
  theme: AITTheme;
}
interface State {
  orderCheckSaving: boolean;
  journalCreateSaving: boolean;
  historyDetailLoading: boolean;
  tripDetail: Trip | null;
}

interface TripJson {
  id: number;
}

interface OrderHistoriesResponse {
  trip: TripJson;
}

interface OrderCheckResponseItem {
  id: number;
  checked_at: string | null;
  checker: { name: string | null };
}

interface OrderCheckResponse {
  orders: OrderCheckResponseItem[];
}

interface JournalCreateResponse {
  journal_created_at: string;
  journal_creator: { name: string };
}

class TripBlock extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      orderCheckSaving: false,
      journalCreateSaving: false,
      historyDetailLoading: false,
      tripDetail: null
    };
  }

  getTraceIds(): number[] {
    if (!this.state.tripDetail) {
      return [];
    }
    return _.uniq(_.flatten(this.state.tripDetail.orders.map(o => o.orderItems.map(i => i.traceId || 0))));
  }

  getData(): (OrderItem | null)[][] {
    const data: (OrderItem | null)[][] = [[]];
    const traceIds = this.getTraceIds();
    const trip = this.state.tripDetail;
    if (!trip) {
      return [];
    }
    for (let i = 0; i < trip.orders.length; i += 1) {
      const o = trip.orders[i];
      if (!data[i]) {
        data[i] = [];
        for (let j = 0; j < traceIds.length; j += 1) {
          const traceId = traceIds[j];
          data[i][traceId] = null;
        }
      }
      for (let j = 0; j < o.orderItems.length; j += 1) {
        const item = o.orderItems[j];
        data[i][item.traceId || 0] = item;
      }
    }
    return data;
  }

  showCheckBox(items: (OrderItem | null)[]): boolean {
    return items.every(item => item === null || item.billedAt !== null);
  }

  async toggleOrderCheck(order: Order): Promise<void> {
    this.setState({ orderCheckSaving: true });
    return Fetcher.put<OrderCheckResponse>(`/arrangement/order_check/${order.id}`, {}).then(response => {
      const tripDetail = this.state.tripDetail;
      response.orders.forEach(updated => {
        const target = tripDetail ? tripDetail.orders.find(o => o.id === updated.id) : null;
        if (!target) {
          return;
        }
        if (updated.checked_at === null) {
          target.checker = null;
          target.checkedAt = null;
        } else {
          target.checker = new Arranger({
            first_name: '',
            last_name: '',
            email: '',
            name: updated.checker.name ?? ''
          });
          target.checkedAt = moment(updated.checked_at);
        }
      });
      this.setState({
        orderCheckSaving: false
      });
    });
  }

  async toggleJournalCreate(order: Order): Promise<void> {
    this.setState({ journalCreateSaving: true });
    return Fetcher.put<JournalCreateResponse>(`/arrangement/journal_create/${order.id}`, {}).then(response => {
      if (response.journal_created_at === null) {
        order.journalCreator = null;
        order.journalCreatedAt = null;
      } else {
        order.journalCreator = new Arranger({
          first_name: '',
          last_name: '',
          email: '',
          ...response.journal_creator
        });
        order.journalCreatedAt = moment(response.journal_created_at);
      }
      this.setState({
        journalCreateSaving: false
      });
    });
  }

  async loadHistoryDetail(trip_id: number): Promise<void> {
    this.setState({ historyDetailLoading: true });
    return Fetcher.get<OrderHistoriesResponse>(`/arrangement/order_histories/${trip_id}.json`).then(response =>
      this.setState({
        tripDetail: new Trip(response.trip),
        historyDetailLoading: false
      })
    );
  }

  render() {
    const trip = this.props.trip;
    const tripDetail = this.state.tripDetail;
    const user = trip.user;
    const organizationName = trip.organization_name_for_history ?? null;
    const traceIds = this.getTraceIds();
    const data = this.getData();
    const classBase = 'order-histories__trip-block';
    const theme = this.props.theme as AITTheme;
    const styles = getStyles(theme);

    return (
      <div className={classBase}>
        <FixedHeader>
          <div>
            <span className={`${classBase}__title-label`} style={styles.tag}>
              <span className={`${classBase}__title-label__name`}>trip_id</span>
              <span className={`${classBase}__title-label__value`}>{trip.id}</span>
            </span>
            <span className={`${classBase}__title-label`} style={styles.tag}>
              <span className={`${classBase}__title-label__name`}>受付日</span>
              <span className={`${classBase}__title-label__value`}>
                {trip.receivedAt ? trip.receivedAt.format('YYYY/MM/DD') : null}
              </span>
            </span>
            <span className={`${classBase}__title-label`} style={styles.tag}>
              <span className={`${classBase}__title-label__name`}>法人</span>
              <span className={`${classBase}__title-label__value`}>{organizationName}</span>
            </span>
            <span className={`${classBase}__title-label`} style={styles.tag}>
              <span className={`${classBase}__title-label__name`}>手配者</span>
              <span className={`${classBase}__title-label__value`}>{user.name}</span>
            </span>
            <span>
              <a
                style={styles.link}
                href={`/arrangement/virtual_counter?trip_id=${trip.id}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                チャット
              </a>
            </span>
          </div>
          <div className={`${classBase}__travelers-area`}>
            <span className={`${classBase}__title-label__name`}>出張者</span>
            {trip.travelerInformations.list.map(ti => (
              <span key={ti.id} className={`${classBase}__traveler-name`} style={styles.tag}>
                {ti.name}
              </span>
            ))}
            <span>
              {tripDetail ? (
                <a style={styles.link} onClick={() => this.setState({ tripDetail: null })}>
                  詳細を閉じる
                </a>
              ) : (
                <a style={styles.link} onClick={() => this.loadHistoryDetail(trip.id)}>
                  詳細を開く
                </a>
              )}
            </span>
          </div>
        </FixedHeader>
        {this.state.historyDetailLoading ? (
          <SimpleLoading />
        ) : tripDetail ? (
          <table className={`${classBase}__table`}>
            <FixedTableHeader>
              <tr>
                <th>Order</th>
                {traceIds.map(id => (
                  <th key={id}>
                    <div>trace_id: {id}</div>
                    <TraceIdMemo traceId={id} />
                  </th>
                ))}
              </tr>
            </FixedTableHeader>
            <tbody>
              {data.map((items, i: number) => {
                const order = tripDetail.orders[i];
                return (
                  <tr key={i}>
                    <td className={`${classBase}__order-td`}>
                      <div>order_id: {order.id}</div>
                      <div>作成者: {order.arranger ? order.arranger.name : null}</div>
                      <div>作成日: {order.createdAt.format('YYYY/MM/DD HH:mm')}</div>
                      <div>
                        請求確定日: {order.confirmedAt ? order.confirmedAt.format('YYYY/MM/DD HH:mm') : null}
                      </div>
                      <div>カード取引ID: {order.paymentTransactionId}</div>
                      <JournalTable order={order} />
                      {this.showCheckBox(items) && (
                        <>
                          <div className={`${classBase}__order-check-area`}>
                            <label className={`${classBase}__order-check`}>
                              {this.state.orderCheckSaving ? (
                                <SimpleLoading />
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={order.checkedAt !== null}
                                  onChange={() =>
                                    this.state.orderCheckSaving ? null : this.toggleOrderCheck(order)
                                  }
                                />
                              )}
                              右ナビチェック
                            </label>
                            {order.checkedAt && (
                              <div>
                                {order.checkedAt.format('YYYY/MM/DD HH:mm ')}
                                {order.checker && order.checker.name}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className={`${classBase}__order-check`}>
                              {this.state.journalCreateSaving ? (
                                <SimpleLoading />
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={order.journalCreatedAt !== null}
                                  onChange={() =>
                                    this.state.journalCreateSaving ? null : this.toggleJournalCreate(order)
                                  }
                                />
                              )}
                              仕訳入力
                            </label>
                            {order.journalCreatedAt && (
                              <div>
                                {order.journalCreatedAt.format('YYYY/MM/DD HH:mm ')}
                                {order.journalCreator && order.journalCreator.name}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                    {traceIds.map(traceId => {
                      const item = items[traceId];
                      return (
                        <td key={traceId}>
                          {item == null ? null : (
                            <>
                              <TraceIdEditor orderItem={item} />
                              <OrderItemCell
                                item={item}
                                suppliedItems={this.props.suppliedItems}
                                paymentMethods={this.props.paymentMethods}
                                traceItems={order.traceItemDescriptions()}
                                trip={this.state.tripDetail}
                              />
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </div>
    );
  }
}

const FixedHeader = styled.div`
  position: sticky;
  padding: 10px;
  top: 190px;
  min-height: 65px;
  background: #fff;
`;

const FixedTableHeader = styled.thead`
  position: sticky;
  top: 248px;
  background: #fff;
  z-index: 1;
`;

const TripBlockWrapper = (props: Omit<Props, 'theme'>) => {
  const theme = useTheme<AITTheme>();
  return <TripBlock {...props} theme={theme} />;
};

export default TripBlockWrapper;
